import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {
	IPrediction,
	IPredictionApiProvider,
	ISavePredictionParams,
	IGetContestPredictionParams,
	IGetPredictionsParams,
	ITransferGuestPredictionsParams,
} from "data/providers/api/predictions.api.provider";
import {RequestState} from "data/enums";

export interface IPredictionsStore {
	get getIsLoading(): boolean;
	get prediction(): IPrediction;
	get savedPlayerId(): number;
	get isPredictionSaved(): boolean;
	get isReadyForUpdate(): boolean;
	get isFirstPrediction(): boolean;

	getPredictionByContestId(contestId: number | undefined): IPrediction | undefined;
	setPrediction: (payload: IPrediction) => void;
	fetchAllPredictions(params?: IGetPredictionsParams): Promise<void>;
	fetchPredictionByContest(params: IGetContestPredictionParams): Promise<void>;
	savePrediction(payload: ISavePredictionParams): Promise<void>;

	transferGuestPredictions(payload: ITransferGuestPredictionsParams): Promise<void>;
	clearSavedState(): void;
	clearFirstPrediction(): void;
}

@injectable()
export class PredictionsStore implements IPredictionsStore {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _prediction: IPrediction = {contestId: 0, playerId: 0};
	@observable private _predictions: IPrediction[] | null = null;
	@observable private _savedPlayerId: number = 0;
	@observable _isPredictionSaved: boolean = false;
	@observable _isReadyForUpdate: boolean = false;
	@observable _isFirstPrediction: boolean = false;

	get prediction() {
		return this._prediction;
	}

	get savedPlayerId() {
		return this._savedPlayerId;
	}

	get getIsLoading(): boolean {
		return this._requestState === RequestState.PENDING;
	}

	get isPredictionSaved() {
		return this._isPredictionSaved;
	}

	get isReadyForUpdate() {
		return this._isReadyForUpdate;
	}

	get isFirstPrediction(): boolean {
		return this._isFirstPrediction;
	}

	@action getPredictionByContestId(contestId: number | undefined) {
		return this._predictions?.find((prediction) => prediction.contestId === contestId);
	}

	constructor(
		@inject(Bindings.PredictionApiProvider)
		private _predictionApiProvider: IPredictionApiProvider
	) {
		makeAutoObservable(this);
	}

	@action clearSavedState() {
		this._isReadyForUpdate = false;
		this._isPredictionSaved = false;
	}

	clearFirstPrediction() {
		runInAction(() => {
			this._isFirstPrediction = false;
		});
	}

	@action setPrediction = (payload: IPrediction) => {
		if (this._prediction.playerId === payload.playerId) return;

		this._prediction = payload;

		if (this._savedPlayerId === payload.playerId) {
			this._isReadyForUpdate = false;
			return;
		}

		if (this._isPredictionSaved) {
			this._isReadyForUpdate = true;
		}
	};

	@action
	async fetchAllPredictions(params?: IGetPredictionsParams) {
		const {data} = await this._predictionApiProvider.predictions(params);

		runInAction(() => {
			this._predictions = data.success.predictions;
		});
	}

	@action
	async fetchPredictionByContest(params: IGetContestPredictionParams) {
		this._requestState = RequestState.PENDING;
		const {data} = await this._predictionApiProvider.get(params);

		runInAction(() => {
			this._prediction = data.success.prediction;
			this._savedPlayerId = data.success.prediction.playerId;

			this._isFirstPrediction = !this._prediction.playerId;
			this._isPredictionSaved = !!this._prediction.playerId;

			this._requestState = RequestState.SUCCESS;
		});
	}

	@action
	async savePrediction(payload: ISavePredictionParams) {
		try {
			this._requestState = RequestState.PENDING;
			const {data} = await this._predictionApiProvider.save(payload);

			runInAction(() => {
				this._prediction = data.success.prediction;
				this._savedPlayerId = data.success.prediction.playerId;
				this._isPredictionSaved = true;
				this._isReadyForUpdate = false;

				this._requestState = RequestState.SUCCESS;
			});
		} catch (err) {
			if (err) {
				this._requestState = RequestState.ERROR;
			}

			throw err;
		}
	}

	@action
	async transferGuestPredictions(payload: ITransferGuestPredictionsParams) {
		const {data} = await this._predictionApiProvider.transfer(payload);

		runInAction(() => {
			this._predictions = data.success.predictions;
		});
	}
}
